import * as apiAuth from '@/api/auth';
import { App, Button, Checkbox, Col, Form, Input, Row, Select, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router";
const { Title, Text } = Typography;
import { validateLegalString } from '@/utils/illegalStringValidator'
import * as authApi from '@/api/auth'
import { useRequest } from 'ahooks';
import { useTranslation } from 'react-i18next';
import { isAcademicOnly } from '@/config/platform';

const Register: React.FC = () => {
    const { message } = App.useApp();
    const navigate = useNavigate();
    const { t } = useTranslation('common');

    const [form] = Form.useForm<{
        nickName: string;
        firstName: string;
        lastName: string;
        email: string;
        verificationCode: string;
        password: string;
        confirm: string;
        phoneNumber: string;
        region: string;
        confirmedPassword: string;
        title: string;
        institute: string;
        isSubscribed: boolean;
    }>();

    const onFinish = async () => {
        try {
            const values = await form.validateFields();
            const res = await apiAuth.register({
                nickName: values.nickName,
                first_name: values.firstName,
                last_name: values.lastName,
                email: values.email,
                verification_code: values.verificationCode,
                password: values.password,
                confirm: values.confirm,
                phone_number: values.phoneNumber,
                region: values.region,
                title: values.title,
                institute: values.institute,
                is_subscribed: values.isSubscribed,
            });
            if (res?.ok) {
                // On an academic-only deployment (Lab@XMU) registration does NOT
                // grant access -- the account is verified first -- so saying "please
                // log in" would send the user straight into "User does not have
                // access". The Hub grants access on registration, where the original
                // copy is correct; hence this is gated on the platform, not global.
                if (isAcademicOnly) {
                    message.success(t('register.register_success_pending'), 12);
                } else {
                    message.success(t('register.register_success'));
                }
                navigate('/login');
            } else {
                message.error(res?.message);
            }
        } catch (error) {
            console.error('Validation failed:', error);
        }

    };

    // Same source and fallback as login.tsx. Without this the terms line
    // rendered the literal '{{title}}' because t() got no interpolation value.
    const websiteTitle = import.meta.env.VITE_WEB_TITLE ?? 'Aitomistic';

    const [regions, setRegions] = React.useState<apiAuth.Region[]>([]);

    useEffect(() => {
        const fetchRegions = async () => {
            const regions = await apiAuth.getRegions();
            if (regions?.ok) {
                setRegions(regions.data);
            }
        };
        fetchRegions();
    }, []);

    const [countdown, setCountdown] = useState(0);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    const { loading: sendVerificationLoading, run: runSendVerification } = useRequest(async () => {
        return await authApi.sendRegisterVerificationCode(form.getFieldValue('email'));
    }, {
        manual: true,
        onError: (error) => {
            console.error('Error sending verification code:', error);
            message.error(t('register.send_code_failed'));
        },
        onSuccess: (data) => {
            if (!data?.ok) {
                message.error(data?.message || t('register.send_code_failed'));
            } else {
                message.success(t('register.send_code_success'));
                setCountdown(60);
            }
        },
    })

    // h-screen + overflow-y-auto makes the outer div the scroll container; the
    // inner min-h-full flex centres the card when it fits and lets the page
    // scroll (top reachable, nothing clipped) when it is taller than the viewport.
    return (
        <div className="h-screen overflow-y-auto bg-gray-100 dark:bg-[#1E1E1E]">
            <div className="flex items-center justify-center min-h-full py-8">
                <div className="w-full max-w-md bg-white dark:bg-[#252526] p-8 rounded-lg shadow-md">
                <Title level={3} className="text-center mb-4">
                    {t('register.welcome')}
                </Title>
                <Text className="block text-center text-gray-500 dark:text-[#999999] mb-6">
                    {t('register.sign_in_subtitle')}
                </Text>
                <Form
                    name="register"
                    form={form}
                    onFinish={onFinish}
                    scrollToFirstError
                    initialValues={{
                        isSubscribed: true,
                    }}
                    layout="vertical"
                >
                    <Form.Item
                        name="nickName"
                        label={t('register.nick_name')}
                        rules={[
                            {
                                required: true,
                                validator(_rule, value, _callback) {
                                    if (!value || value == "") {
                                        return Promise.reject(t('register.nick_name_empty'))
                                    }

                                    if (!validateLegalString(value).isValid) {
                                        return Promise.reject(t('register.nick_name_illegal'))
                                    }

                                    if (value.length <= 0 || value.length > 15) {
                                        return Promise.reject(t('register.nick_name_length'))
                                    }

                                    if (!/^[a-z]/.test(value)) {
                                        return Promise.reject(t('register.nick_name_start'));
                                    }

                                    if (!/^[a-z0-9_]+$/.test(value)) {
                                        return Promise.reject(t('register.nick_name_chars'));
                                    }
                                    return Promise.resolve()
                                },
                            }
                        ]}
                        hasFeedback
                    >
                        <Input />
                    </Form.Item>
                    <Row gutter={8}>
                        <Col>
                            <Form.Item
                                name="firstName"
                                label={t('register.first_name')}
                                style={{ width: 185 }}
                                rules={[
                                    {
                                        required: true,
                                        message: t('register.first_name_required'),
                                    },
                                ]}
                                hasFeedback
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col>
                            <Form.Item
                                name="lastName"
                                label={t('register.last_name')}
                                style={{ width: 185 }}
                                rules={[
                                    {
                                        required: true,
                                        message: t('register.last_name_required'),
                                    },
                                ]}
                                hasFeedback
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        name="email"
                        label={t('register.email')}
                        extra={isAcademicOnly ? t('register.academic_notice') : undefined}
                        rules={[
                            {
                                type: 'email',
                                message: t('register.email_invalid'),
                            },
                            {
                                required: true,
                                message: t('register.email_required'),
                            },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label={t('register.verification_code')}
                        name="verificationCode"
                        rules={[
                            {
                                required: true,
                                validator: async (_rule, value) => {
                                    if (!value || value.length !== 6) {
                                        return Promise.reject(t('register.code_invalid'));
                                    }
                                }
                            },
                        ]}
                    >
                        <div className="flex gap-2">
                            <Form.Item noStyle name="verificationCode">
                                <Input
                                    autoComplete="off"
                                    className="flex-1"
                                    placeholder={t('register.code_placeholder')}
                                    maxLength={6}
                                />
                            </Form.Item>
                            <Button
                                type="primary"
                                loading={sendVerificationLoading}
                                disabled={countdown > 0}
                                onClick={runSendVerification}
                                className="whitespace-nowrap"
                            >
                                {countdown > 0 ? `${countdown}s` : t('register.get_code')}
                            </Button>
                        </div>
                        <Text type="secondary" className="text-xs mt-1">
                            {t('register.check_spam')}
                        </Text>
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label={t('register.password')}
                        rules={[
                            {
                                required: true,
                                message: t('register.password_required'),
                            },
                            {
                                required: true,
                                validator: async (_rule, value) => {
                                    if (!value || value.length < 8 || value.length > 20) {
                                        return Promise.reject(t('register.password_length'));
                                    }

                                    if (value && !/[A-Z]/.test(value)) {
                                        return Promise.reject(t('register.password_uppercase'));
                                    }
                                    if (value && !/[a-z]/.test(value)) {
                                        return Promise.reject(t('register.password_lowercase'));
                                    }
                                    if (value && !/[0-9]/.test(value)) {
                                        return Promise.reject(t('register.password_digit'));
                                    }

                                    const res = await authApi.verifyPasswordStrength({ password: value });
                                    if (!res?.ok) {
                                        return Promise.reject(t('register.password_weak'));
                                    }
                                },
                            }
                        ]}
                        hasFeedback
                    >
                        <Input.Password data-testid="password"/>
                    </Form.Item>

                    <Form.Item
                        name="confirm"
                        label={t('register.confirm_password')}
                        dependencies={['password']}
                        hasFeedback
                        rules={[
                            {
                                required: true,
                                message: t('register.confirm_required'),
                            },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(t('register.confirm_mismatch')));
                                },
                            }),
                        ]}
                    >
                        <Input.Password data-testid="confirm"/>
                    </Form.Item>
                    <Form.Item
                        name="phoneNumber"
                        label={t('register.phone_number')}
                        rules={[
                            {
                                required: true,
                                message: t('register.phone_required'),
                            },
                        ]}
                        hasFeedback
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item label={t('register.region')} name="region"
                        rules={[
                            {
                                required: true,
                                message: t('register.region_required'),
                            },
                        ]}
                        hasFeedback
                    >
                        <Select
                            showSearch
                            placeholder={t('register.region_placeholder')}
                            optionFilterProp="name"
                            fieldNames={{ label: 'name', value: 'name' }}
                            options={regions}
                        />
                    </Form.Item>
                    <Form.Item
                        name="institute"
                        label={t('register.institute')}
                        rules={[
                            {
                                required: true,
                                message: t('register.institute_required'),
                            },
                        ]}
                        hasFeedback
                    >
                        <Input />
                    </Form.Item>
                    {/* Marketing opt-in belongs on the commercial Hub, not the
                        academic Lab; hidden when the platform is academic-only. */}
                    {!isAcademicOnly && (
                    <Form.Item name="isSubscribed" valuePropName="checked">
                        <Checkbox>
                            <span className='text-gray-400 dark:text-[#858585]'>{t('register.subscribe')}</span>
                        </Checkbox>
                    </Form.Item>
                    )}
                    <p className='text-gray-400 dark:text-[#858585] mb-2 text-center'>
                        {t('register.terms_prefix', { title: websiteTitle })} <a href="/legal?type=terms" className='underline'>{t('register.terms')}</a>, <a href="/legal?type=privacy" className='underline'>{t('register.privacy')}</a> {t('register.terms_and')} <a href="/legal?type=cookies" className='underline'>{t('register.cookies')}</a>.
                    </p>
                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            className="w-full bg-blue-500 dark:bg-[#0E639C] text-white py-2 rounded-md hover:bg-blue-600 focus:outline-none"
                        >
                            {t('register.register_button')}
                        </Button>
                    </Form.Item>
                </Form>
                </div>
            </div>
        </div>
    );
};

export default Register;